import { Link } from "react-router-dom"

import { siteConfig } from "@/components/homepage/_config"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { DitherDivider } from "@/components/homepage/animations/lazy-dither"
import { CornerPlus } from "@/components/ui/corner-plus"

export function Pricing({ isAuthenticated }: { isAuthenticated?: boolean }) {
    const { pricing } = siteConfig

    // Order: Free, Pro, Max, Ultra
    const plans = [
        pricing.pricingItems.find((p) => p.name === "Free")!,
        pricing.pricingItems.find((p) => p.name === "Pro")!,
        pricing.pricingItems.find((p) => p.name === "Max")!,
        pricing.pricingItems.find((p) => p.name === "Ultra")!,
    ].filter(Boolean)

    const getCtaHref = (plan: (typeof pricing.pricingItems)[0]) => {
        if (isAuthenticated) return "/settings"
        return plan.href
    }

    return (
        <section id="pricing" className="relative w-full">
            <DitherDivider />
            <div className="mx-auto">
                {/* Centered header */}
                <div className="text-center px-6 md:px-10 lg:px-14 pt-14 md:pt-20 pb-10 border-t border-border">
                    <h3 className="text-3xl font-medium tracking-tighter md:text-4xl">
                        {pricing.title}
                    </h3>
                    <p className="mt-4 text-muted-foreground text-balance max-w-md mx-auto">
                        {pricing.description}
                    </p>
                </div>

                {/* 3-column grid with dividers */}
                <div className="grid grid-cols-1 md:grid-cols-4 md:divide-x divide-border border-t border-border">
                    {plans.map((plan, i) => {
                        const isPro = plan.name === "Pro"
                        const isMax = plan.name === "Max"
                        const isUltra = plan.name === "Ultra"
                        const isFree = plan.name === "Free"

                        return (
                            <div
                                key={plan.name}
                                className={cn(
                                    "relative flex flex-col p-6 md:p-8 pb-14 border-b border-border",
                                    isPro && "bg-sky-50/30"
                                )}
                            >
                                {i > 0 && (
                                    <CornerPlus position="top-left" className="text-muted-foreground/50 hidden md:block" />
                                )}
                                {i < plans.length - 1 && (
                                    <CornerPlus position="top-right" className="text-muted-foreground/50 hidden md:block" />
                                )}

                                {/* Plan name + badge */}
                                <div className="flex items-center gap-2 mb-6">
                                    <h4 className="text-xl font-medium">{plan.name}</h4>
                                    {plan.isPopular && (
                                        <span className="rounded-md border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                                            Popular
                                        </span>
                                    )}
                                </div>

                                {/* Price */}
                                <div className="flex items-baseline gap-2 mb-2">
                                    <span className="text-3xl font-medium md:text-4xl">
                                        {plan.price}
                                    </span>
                                    <span className="text-muted-foreground">
                                        /{plan.period}
                                    </span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-6">
                                    {plan.description}
                                </p>

                                {/* CTA Button */}
                                <Button
                                    size="lg"
                                    asChild
                                    variant="outline"
                                    className={cn(
                                        "w-full cursor-pointer rounded-full transition-all duration-300 ease-in-out hover:scale-[1.02] mb-6",
                                        isPro
                                            ? "bg-sky-500 text-white hover:bg-sky-500/90 hover:text-white"
                                            : isMax
                                            ? "bg-foreground text-primary-foreground hover:bg-foreground/80 hover:text-primary-foreground"
                                            : isUltra
                                            ? "bg-fuchsia-500 text-white hover:bg-fuchsia-500/90 hover:text-white"
                                            : "border border-border bg-card text-secondary-foreground hover:bg-accent",
                                    )}
                                >
                                    <Link to={getCtaHref(plan)}>
                                        {plan.buttonText}
                                    </Link>
                                </Button>

                                <hr className="border-dashed border-border mb-6" />

                                {/* Features */}
                                <div className="flex flex-col gap-4 flex-1">
                                    {isPro && (
                                        <p className="text-sm font-medium text-muted-foreground">
                                            Everything in Free +
                                        </p>
                                    )}
                                    {isMax && (
                                        <p className="text-sm font-medium text-muted-foreground">
                                            Everything in Pro +
                                        </p>
                                    )}
                                    {isUltra && (
                                        <p className="text-sm font-medium text-muted-foreground">
                                            Everything in Max +
                                        </p>
                                    )}
                                    {isFree && (
                                        <p className="text-sm font-medium text-muted-foreground">
                                            Get started today:
                                        </p>
                                    )}
                                    <ul className="space-y-2">
                                        {plan.features.map((feature, idx) => (
                                            <li
                                                key={idx}
                                                className="flex items-start gap-2"
                                            >
                                                <svg
                                                    className="mt-0.5 h-5 w-5 shrink-0 text-secondary-foreground"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span className="text-sm text-secondary-foreground">
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
            <DitherDivider />
        </section>
    )
}
